import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  BadgeEuro, CreditCard, FileDown, FileText, Link as LinkIcon, PieChart, ReceiptEuro,
  ShieldCheck, Wallet,
} from 'lucide-react'
import { useI18n } from '../i18n'
import { useStore } from '../data/store'
import { addMonths, monthStart, todayISO } from '../lib/dates'
import { Badge, Button, Card, EmptyState, Select, Stat } from '../ui/kit'
import CollectPayment from './CollectPayment'
import BookkeepingView from './BookkeepingView'
import type { Invoice } from '../data/model'
import { oeffneExtern } from '../lib/extern'
import { oeffentlicheUrl } from '../lib/urls'

type Tab = 'invoices' | 'quotes' | 'deposits' | 'touristTax' | 'books'

export default function BillingView() {
  const { t, lang, formatMoney, formatDate, pick } = useI18n()
  const de = lang === 'de'
  const store = useStore()
  const navigate = useNavigate()
  const [tab, setTab] = useState<Tab>('invoices')
  const [collect, setCollect] = useState<Invoice | null>(null)
  const [copiedQuote, setCopiedQuote] = useState<string | null>(null)

  /**
   * Was auf eine Rechnung noch offen ist.
   *
   * `paid_amount` kennt nur Zahlungen, die auf die Rechnung gebucht wurden. Die
   * Anzahlung hängt aber an der BUCHUNG — sie wird geleistet, lange bevor es eine
   * Rechnung gibt. Wer sie hier nicht mitzählt, mahnt einen Betrag ein, den der Gast
   * längst überwiesen hat, und bietet ihm oben drauf einen Zahlungslink dafür an.
   * Dieselbe Rechnung steht auch im Beleg selbst (DocumentView).
   */
  const outstandingOf = (inv: typeof store.db.invoices[number]) => {
    const paid = inv.booking_id ? store.paidFor(inv.booking_id) : inv.paid_amount
    return Math.round((inv.total_gross - paid) * 100) / 100
  }

  const openTotal = store.db.invoices
    .filter((i) => i.status !== 'paid' && i.status !== 'cancelled')
    .reduce((a, i) => a + Math.max(0, outstandingOf(i)), 0)

  const depositsHeld = store.db.bookings
    .filter((b) => b.deposit_status === 'held' || b.deposit_status === 'partially_retained')
    .reduce((a, b) => a + b.deposit_amount, 0)

  const tabs: [Tab, string, React.ReactNode][] = [
    ['invoices', t('invoice.title'), <ReceiptEuro size={15} key="i" />],
    ['quotes', t('quote.title'), <FileText size={15} key="q" />],
    ['deposits', t('deposit.title'), <ShieldCheck size={15} key="d" />],
    ['touristTax', t('touristTax.title'), <BadgeEuro size={15} key="t" />],
    ['books', t('books.title'), <PieChart size={15} key="b" />],
  ]

  return (
    <div className="p-4 pt-safe space-y-4 max-w-3xl">
      <h1 className="text-xl font-semibold">{t('nav.billing')}</h1>

      <div className="grid grid-cols-2 gap-3">
        <Stat label={t('dashboard.openInvoices')} value={formatMoney(openTotal)}
          tone={openTotal > 0 ? 'text-warn' : undefined} />
        <Stat label={`${t('deposit.title')} · ${t('deposit.held')}`} value={formatMoney(depositsHeld)} />
      </div>

      <div className="flex gap-1 bg-surface-2 p-1 rounded-xl overflow-x-auto">
        {tabs.map(([key, label, icon]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={
              'flex-1 whitespace-nowrap flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm ' +
              (tab === key ? 'bg-surface font-medium shadow-sm' : 'text-muted')
            }
          >
            {icon}{label}
          </button>
        ))}
      </div>

      {tab === 'invoices' && (
        store.db.invoices.length === 0
          ? <Card><EmptyState title={t('common.noResults')}
              hint="Rechnungen entstehen aus einer Buchung heraus." /></Card>
          : <div className="space-y-2">
              {(() => {
                const drafts = store.db.invoices.filter((i) => !i.issued_at)
                const issued = store.db.invoices.filter((i) => i.issued_at)
                return (
                  <>
                    {drafts.length > 0 && (
                      <h3 className="text-sm font-medium text-muted pt-1">
                        {t('invoice.drafts')} ({drafts.length})
                      </h3>
                    )}
                    {drafts.map((inv) => (
                      <Card key={inv.id} className="p-3 border-warn/40">
                        <div className="flex items-center gap-3">
                          <div className="min-w-0 flex-1">
                            <div className="font-medium">{t('invoice.draft')}</div>
                            <div className="text-xs text-muted">
                              {store.guestName(inv.guest_id)} · {formatDate(inv.issue_date)}
                            </div>
                          </div>
                          <div className="tabular-nums font-medium">{formatMoney(inv.total_gross)}</div>
                        </div>
                        <div className="mt-3 flex gap-2">
                          <Button size="sm" onClick={() => navigate(`/document/invoice/${inv.id}`)}>
                            <FileDown size={15} /> {t('common.open')}
                          </Button>
                          <Button size="sm" variant="primary"
                            onClick={() => store.issueInvoice(inv.id)}>
                            {t('invoice.issue')}
                          </Button>
                        </div>
                      </Card>
                    ))}
                    {drafts.length > 0 && issued.length > 0 && (
                      <h3 className="text-sm font-medium text-muted pt-3">
                        {t('invoice.archive')} ({issued.length})
                      </h3>
                    )}
                  </>
                )
              })()}
              {store.db.invoices.filter((i) => i.issued_at).map((inv) => {
                const outstanding = outstandingOf(inv)
                const prepaid = inv.booking_id
                  ? store.db.payments
                    .filter((p) => p.booking_id === inv.booking_id && p.kind === 'prepayment')
                    .reduce((a, p) => a + p.amount, 0)
                  : 0
                return (
                  <Card key={inv.id} className="p-3">
                    <div className="flex items-center gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="font-medium">{inv.number}</div>
                        <div className="text-xs text-muted">
                          {store.guestName(inv.guest_id)} · {t('invoice.issued')} {formatDate(inv.issue_date)}
                          {inv.due_date && ` · ${t('invoice.due')} ${formatDate(inv.due_date)}`}
                          {prepaid > 0 && (
                            <> · {t('booking.prepayment')} −{formatMoney(prepaid)}</>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="tabular-nums font-medium">{formatMoney(inv.total_gross)}</div>
                        <Badge tone={inv.status === 'cancelled' ? 'neutral'
                          : inv.type === 'credit_note' ? 'info'
                          : outstanding <= 0.004 ? 'ok'
                          : inv.due_date && inv.due_date < todayISO() ? 'danger' : 'warn'}>
                          {inv.status === 'cancelled' ? t('invoice.cancelled')
                            : inv.type === 'credit_note' ? t('invoice.creditNote')
                            : outstanding <= 0.004 ? t('common.paid')
                            : inv.due_date && inv.due_date < todayISO() ? t('invoice.overdue')
                            : t('common.open')}
                        </Badge>
                      </div>
                    </div>
                    <div className="mt-3 flex gap-2">
                      <Button size="sm" onClick={() => navigate(`/document/invoice/${inv.id}`)}>
                        <FileDown size={15} /> PDF
                      </Button>
                      {inv.type !== 'credit_note' && inv.status !== 'cancelled'
                        && !store.db.invoices.some((c) => c.corrects_invoice_id === inv.id) && (
                        <Button size="sm" variant="danger" onClick={async () => {
                          const reason = window.prompt(t('invoice.cancelReason')) ?? ''
                          const noteId = await store.cancelInvoice(inv.id, reason)
                          if (noteId) navigate(`/document/invoice/${noteId}`)
                        }}>
                          {t('invoice.cancel')}
                        </Button>
                      )}
                      {outstanding > 0 && inv.type !== 'credit_note'
                        && store.db.tenant.stripe_account_id && (
                        <Button size="sm" variant="secondary" onClick={async () => {
                          const url = await store.guestPaymentLink(inv.id)
                          // War `window.open`, und das wird in einer WebView
                          // blockiert — der Knopf tat dort nichts, ohne Meldung.
                          if (url) await oeffneExtern(url)
                        }}>
                          <CreditCard size={15} /> {t('invoice.payLink')}
                        </Button>
                      )}
                      {/* Kassieren statt „als bezahlt markieren": der alte Knopf
                          buchte stillschweigend „heute, per Überweisung" — und auf
                          der Rechnung stand nachher nichts davon. */}
                      {outstanding > 0 && inv.type !== 'credit_note' && (
                        <Button size="sm" variant="primary" onClick={() => setCollect(inv)}>
                          <Wallet size={15} /> {t('invoice.collect')}
                        </Button>
                      )}
                    </div>
                  </Card>
                )
              })}
            </div>
      )}

      {tab === 'quotes' && (
        store.db.quotes.length === 0
          ? <Card><EmptyState title={t('common.noResults')}
              hint="Angebote entstehen aus einer Anfrage im Kalender." /></Card>
          : <div className="space-y-2">
              {store.db.quotes.map((q) => (
                <Card key={q.id} className="p-3">
                  <div className="flex items-center gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="font-medium">{q.number}</div>
                      <div className="text-xs text-muted">
                        {t('quote.valid_until')} {q.valid_until && formatDate(q.valid_until)}
                        {' · '}{t('booking.prepayment')} {formatMoney(q.prepayment_amount)}
                      </div>
                    </div>
                    {/*
                      Der Stand des ONLINE-Angebots, nicht nur der Status im System.
                      „Verschickt und nie geöffnet" und „gelesen, aber nicht
                      entschieden" sind zwei verschiedene Lagen — die erste ruft nach
                      einer Erinnerung, die zweite nach Geduld. Wer das nicht
                      unterscheiden kann, ruft im falschen Moment an.
                    */}
                    <div className="text-right">
                      <div className="tabular-nums font-medium">{formatMoney(q.total_gross)}</div>
                      <Badge tone={q.accepted_at ? 'ok'
                        : q.declined_at ? 'danger'
                        : q.viewed_at ? 'info' : 'neutral'}>
                        {q.accepted_at ? (de ? 'Angenommen' : 'Accepted')
                          : q.declined_at ? (de ? 'Abgelehnt' : 'Declined')
                          : q.viewed_at ? (de ? 'Geöffnet' : 'Opened')
                          : (de ? 'Noch nicht geöffnet' : 'Not opened')}
                      </Badge>
                    </div>
                  </div>
                  <div className="mt-3 flex gap-2 flex-wrap">
                    <Button size="sm" onClick={() => navigate(`/document/quote/${q.id}`)}>
                      <FileDown size={15} /> {de ? 'Öffnen' : 'Open'}
                    </Button>
                    {store.live && q.public_token && !q.accepted_at && !q.declined_at && (
                      <Button size="sm" variant="secondary" onClick={() => {
                        void navigator.clipboard?.writeText(
                          oeffentlicheUrl(`/q/${q.public_token}`))
                        setCopiedQuote(q.id)
                        window.setTimeout(() => setCopiedQuote(null), 1500)
                      }}>
                        <LinkIcon size={15} />
                        {copiedQuote === q.id
                          ? (de ? 'Kopiert' : 'Copied')
                          : (de ? 'Link kopieren' : 'Copy link')}
                      </Button>
                    )}
                  </div>
                </Card>
              ))}
            </div>
      )}

      <CollectPayment invoice={collect} open={!!collect} onClose={() => setCollect(null)} />

      {tab === 'deposits' && <DepositList />}
      {tab === 'touristTax' && <TouristTaxReport />}
      {tab === 'books' && <BookkeepingView />}

      {/* Der Trenner nur, wenn danach auch etwas kommt: ein Betrieb ohne
          hinterlegte Gemeinde las hier sonst „Ortstaxe ·" und dann nichts. */}
      <p className="text-xs text-muted">
        {pick(store.touristTax.name)}
        {store.touristTax.municipality ? ` · ${store.touristTax.municipality}` : ''}
      </p>
    </div>
  )
}

function DepositList() {
  const { t, formatMoney, formatDate } = useI18n()
  const store = useStore()
  const rows = store.db.bookings.filter((b) => b.deposit_amount > 0)

  if (rows.length === 0) {
    return <Card><EmptyState title={t('common.noResults')} hint={t('deposit.not_required')} /></Card>
  }

  return (
    <div className="space-y-2">
      {/*
        Auf dem Handy stapeln statt quetschen.
        Vorher stand alles in einer Flex-Zeile: das Auswahlfeld ist ein natives
        <select> und gibt seine Breite nicht her, also blieb für Name und Zeitraum
        fast nichts übrig — der Zeitraum brach um und lief in den Betrag hinein.
        Ab `sm` ist genug Platz für eine Zeile, darunter sind es zwei.
      */}
      {rows.map((b) => (
        <Card key={b.id} className="p-3 space-y-2 sm:space-y-0 sm:flex sm:items-center sm:gap-3">
          <div className="min-w-0 sm:flex-1">
            <div className="font-medium truncate">
              {store.guestName(b.guest_id) || b.reference}
            </div>
            <div className="text-xs text-muted truncate">
              {store.unitById(b.unit_id)?.name} · {formatDate(b.arrival)} – {formatDate(b.departure)}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="tabular-nums shrink-0 whitespace-nowrap font-medium">
              {formatMoney(b.deposit_amount)}
            </div>
            <Select
              className="flex-1 sm:flex-none sm:w-auto"
              value={b.deposit_status}
              /*
               * Der Kautionsstand ist der einzige Ort, an dem eine ALTE Buchung
               * geändert wird, ohne dass jemand das Buchungsblatt öffnet. Seit dem
               * Namenszwang kann `saveBooking` ablehnen — bei Altdaten ohne Gast.
               * Die Auswahl springt dann sichtbar zurück, statt einen Stand
               * anzuzeigen, den die Datenbank nie bekommen hat.
               */
              onChange={async (e) => {
                const next = e.target.value as typeof b.deposit_status
                const before = b.deposit_status
                const ok = await store.saveBooking({ ...b, deposit_status: next })
                if (!ok) e.target.value = before
              }}
            >
              {(['due', 'held', 'partially_retained', 'retained', 'released'] as const).map((s) => (
                <option key={s} value={s}>{t(`deposit.${s}` as never)}</option>
              ))}
            </Select>
          </div>
        </Card>
      ))}
    </div>
  )
}

/**
 * The figures a municipality asks for. Derived from the actual stay dates clipped
 * to the selected month, so a stay across a month boundary is split rather than
 * counted twice.
 */
function TouristTaxReport() {
  const { t, formatMoney, lang } = useI18n()
  const store = useStore()
  const [month, setMonth] = useState(() => monthStart(todayISO()))

  const report = useMemo(() => {
    const from = month
    const to = addMonths(month, 1)
    const rule = store.touristTax
    let personNights = 0
    let exemptNights = 0
    let bookings = 0

    for (const b of store.db.bookings) {
      if (b.kind !== 'booking') continue
      if (!['confirmed', 'checked_in', 'checked_out'].includes(b.status)) continue
      const s = b.arrival > from ? b.arrival : from
      const e = b.departure < to ? b.departure : to
      const n = Math.max(0, (new Date(e).getTime() - new Date(s).getTime()) / 86_400_000)
      if (n <= 0) continue
      bookings += 1

      const ages = b.children_ages ?? []
      const exemptChildren = rule.exempt_under_age !== null && ages.length === b.children
        ? ages.filter((a) => a < rule.exempt_under_age!).length
        : 0
      personNights += (b.adults + b.children) * n
      exemptNights += exemptChildren * n
    }

    const liable = personNights - exemptNights
    return {
      bookings, personNights, exemptNights, liable,
      amount: Math.round(liable * rule.amount_per_person_night * 100) / 100,
    }
  }, [month, store])

  const monthLabel = new Intl.DateTimeFormat(lang === 'de' ? 'de-AT' : 'en-GB',
    { month: 'long', year: 'numeric' }).format(new Date(month + 'T00:00:00'))

  return (
    <Card className="p-4 space-y-3">
      <div className="flex items-center gap-2">
        <h2 className="font-medium flex-1">{t('touristTax.report')}</h2>
        <Button size="sm" onClick={() => setMonth(addMonths(month, -1))}>−</Button>
        <span className="text-sm tabular-nums w-32 text-center">{monthLabel}</span>
        <Button size="sm" onClick={() => setMonth(addMonths(month, 1))}>+</Button>
      </div>

      <dl className="grid grid-cols-2 gap-3 text-sm">
        <Row label={t('touristTax.personNights')} value={report.personNights} />
        <Row label={t('touristTax.exempt')} value={report.exemptNights} />
        <Row label={t('touristTax.liable')} value={report.liable} />
        <Row label={t('nav.bookings')} value={report.bookings} />
      </dl>

      <div className="flex items-baseline justify-between border-t border-border pt-3">
        <span className="text-sm text-muted">
          {report.liable} × {formatMoney(store.touristTax.amount_per_person_night)}
        </span>
        <span className="text-xl font-semibold tabular-nums">{formatMoney(report.amount)}</span>
      </div>
      <p className="text-xs text-muted">
        {store.touristTax.municipality ? `${store.touristTax.municipality} · ` : ''}
        {t('touristTax.exemptUnder', {
          n: store.touristTax.exempt_under_age ?? 0,
        })}
      </p>
    </Card>
  )
}

function Row({ label, value }: { label: string; value: number }) {
  return (
    <div className="bg-surface-2 rounded-xl px-3 py-2">
      <dt className="text-xs text-muted">{label}</dt>
      <dd className="text-lg font-medium tabular-nums">{value}</dd>
    </div>
  )
}
